// src/Components/ImportMarkers/ExcelDialogPhoto.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import CircularProgress from '@mui/material/CircularProgress';

import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

import DialogActionBar from './DialogActionBar';
import { MenuUpload, MenuUploadGrey } from '../../assets/icons/CustomIconsAdd2';
import { SoundWaveIcon } from '../../assets/icons/CustomIconsAdd3';

const normalizeLooseString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

export default function ExcelDialogPhoto({ state, actions }) {
  const makeUppercase = state.makeUppercase || false;

  const [applyToAllPhotos, setApplyToAllPhotos] = useState(false);
  const [localSelectedPhotos, setLocalSelectedPhotos] = useState([]);
  const [currentRightPanelLocalFiles, setCurrentRightPanelLocalFiles] = useState([]);

  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [webPhotos, setWebPhotos] = useState([]);
  const [isSearchingWeb, setIsSearchingWeb] = useState(false);
  const [webSearchError, setWebSearchError] = useState('');
  const [isScrapingPhotos, setIsScrapingPhotos] = useState(false);

  const [isOkPressed, setIsOkPressed] = useState(false);
  const submitRef = useRef(null);
  const cancelRef = useRef(null);
  const navPrevRef = useRef(null);
  const navNextRef = useRef(null);
  const navUpRef = useRef(null);
  const navDownRef = useRef(null);
  const enterOriginRef = useRef(null);

  const localFileInputRef = useRef(null);
  const prevRowIdRef = useRef(null);
  const isBulkProcessingRef = useRef(false);
  const cancelLoopRef = useRef(false);

  const spaceTriggeredRef = useRef(false);

  const restoreGridFocus = useCallback(() => {
    if (document.activeElement && document.activeElement.tagName !== 'BODY') {
      document.activeElement.blur();
    }
  }, []);

  const getFilePreview = (file) => {
    if (!file) return '';
    if (file.preview) return file.preview;
    try {
      const url = URL.createObjectURL(file);
      file.preview = url;
      return url;
    } catch (err) {
      return '';
    }
  };

  useEffect(() => {
    const currentRowId = state.manualMatchRowId;
    if (currentRowId !== prevRowIdRef.current) {
      prevRowIdRef.current = currentRowId;

      if (currentRowId && !isBulkProcessingRef.current) {
        setLocalSelectedPhotos([]);
        setWebPhotos([]);
        setWebSearchError('');
        setCurrentRightPanelLocalFiles([]);
        setApplyToAllPhotos(false);

        const row = state.parsedData?.find((r) => r.id === currentRowId);
        if (row) {
          let titleVal = '';
          const titleColKey = Object.keys(state.rowMappings || {}).find(
            (k) =>
              state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
          );
          if (titleColKey) {
            let colName = titleColKey;
            const cRow = state.customRows?.find((r) => r.id === titleColKey);
            if (cRow && cRow.name !== '-') colName = cRow.name;
            else if (titleColKey.startsWith('prop_')) colName = titleColKey.substring(5);

            const actualKey = Object.keys(row).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            if (actualKey && row[actualKey]) titleVal = String(row[actualKey]).trim();
          }
          if (!titleVal) {
            titleVal = String(row['Title'] || row['title'] || row['TITLE'] || '').trim();
          }

          const queryParts = [];
          [
            'TITLE',
            'LOCATION ADDRESS',
            'CITY',
            'STATE',
            'POSTCODE/ZIP',
            'COUNTRY',
          ].forEach((field) => {
            const colKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === field,
            );
            if (colKey) {
              let cName = colKey;
              const cRow = state.customRows?.find((r) => r.id === colKey);
              if (cRow && cRow.name !== '-') cName = cRow.name;
              else if (colKey.startsWith('prop_')) cName = colKey.substring(5);

              const aKey = Object.keys(row).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(cName),
              );
              if (aKey && row[aKey]) queryParts.push(String(row[aKey]).trim());
            }
          });

          if (queryParts.length === 0 && titleVal) {
            queryParts.push(titleVal);
          }
          setWebSearchQuery(queryParts.filter(Boolean).join(', '));
        }
      }
    }
  }, [
    state.manualMatchRowId,
    state.parsedData,
    state.rowMappings,
    state.rowSubMappings,
    state.customRows,
  ]);

  const handleSearchWebPhotos = async () => {
    if (!webSearchQuery.trim()) return;
    setIsSearchingWeb(true);
    setWebSearchError('');
    setWebPhotos([]);

    try {
      const placesLib = await window.google.maps.importLibrary('places');
      const { places } = await placesLib.Place.searchByText({
        textQuery: webSearchQuery,
        fields: ['id', 'displayName', 'photos'],
      });

      if (places && places.length > 0) {
        const urls = [];

        for (const place of places) {
          if (place.photos && place.photos.length > 0) {
            for (const p of place.photos) {
              urls.push(p.getURI({ maxWidth: 800 }));
              if (urls.length >= 20) break;
            }
          }
          if (urls.length >= 20) break;
        }

        if (urls.length > 0) {
          setWebPhotos(urls);
        } else {
          setWebSearchError('No photos found for this location.');
        }
      } else {
        setWebSearchError('No locations found matching that search.');
      }
    } catch (err) {
      console.error('Places Photo API Error:', err);
      setWebSearchError('Failed to search for photos.');
    } finally {
      setIsSearchingWeb(false);
    }
  };

  const handleLocalFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach((f) => {
        f.preview = URL.createObjectURL(f);
      });
      actions.setStagedFiles?.((prev) => [...prev, ...files]);
      setCurrentRightPanelLocalFiles((prev) => [...prev, ...files]);
      setLocalSelectedPhotos((prev) => [...prev, ...files]);
    }
    e.target.value = null;
  };

  const toggleLocalPhotoSelection = (file) => {
    setLocalSelectedPhotos((prev) => {
      const isSelected = prev.some((p) => p.name === file.name);
      if (isSelected) return prev.filter((p) => p.name !== file.name);
      return [...prev, file];
    });
  };

  const toggleWebPhotoSelection = (url, idx) => {
    setLocalSelectedPhotos((prev) => {
      const isSelected = prev.some((p) => p.sourceUrl === url);
      if (isSelected) return prev.filter((p) => p.sourceUrl !== url);
      return [
        ...prev,
        {
          name: `web_photo_temp_${idx}.jpg`,
          sourceUrl: url,
          isGooglePhoto: true,
        },
      ];
    });
  };

  const getPhotoColNameForNext = () => {
    const photoColKey = Object.keys(state.rowMappings || {}).find(
      (k) => state.rowMappings[k] === 'PHOTOS',
    );
    let photoColName = 'Photos';
    if (photoColKey) {
      const cRow = state.customRows?.find((r) => r.id === photoColKey);
      if (cRow && cRow.name !== '-') photoColName = cRow.name;
      else if (photoColKey.startsWith('prop_')) photoColName = photoColKey.substring(5);
    }
    return photoColName;
  };

  const handleManualMatchOk = async (goToNext = false) => {
    if (applyToAllPhotos) {
      isBulkProcessingRef.current = true;
      setIsScrapingPhotos(true);
      cancelLoopRef.current = false;

      window.dispatchEvent(new CustomEvent('loktr-bulk-scrape-start'));

      try {
        const functions = getFunctions(getApp(), 'australia-southeast1');
        const scrapeFunction = httpsCallable(functions, 'scrapeAndStoreWebPhoto');
        const placesLib = await window.google.maps.importLibrary('places');

        const photoColName =
          state.columns?.find(
            (c) => c.toLowerCase().includes('photo') || c.toLowerCase().includes('image'),
          ) || 'Photos';
        const unimportedRows =
          state.parsedData?.filter(
            (r) => !state.successfullyImportedIds?.includes(r.id),
          ) || [];

        for (let i = 0; i < unimportedRows.length; i++) {
          if (cancelLoopRef.current) break;

          const row = unimportedRows[i];

          if (row.photoMatch === 'MATCH' && row.matchedFiles?.length > 0) continue;

          let titleVal = '';
          const titleColKey = Object.keys(state.rowMappings || {}).find(
            (k) =>
              state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
          );
          if (titleColKey) {
            let colName = titleColKey;
            const cRow = state.customRows?.find((r) => r.id === titleColKey);
            if (cRow && cRow.name !== '-') colName = cRow.name;
            else if (titleColKey.startsWith('prop_')) colName = titleColKey.substring(5);

            const actualKey = Object.keys(row).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            if (actualKey && row[actualKey]) titleVal = String(row[actualKey]).trim();
          }
          if (!titleVal) {
            titleVal = String(row['Title'] || row['title'] || row['TITLE'] || '').trim();
          }

          const queryParts = [];
          [
            'TITLE',
            'LOCATION ADDRESS',
            'CITY',
            'STATE',
            'POSTCODE/ZIP',
            'COUNTRY',
          ].forEach((field) => {
            const colKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === field,
            );
            if (colKey) {
              let cName = colKey;
              const cRow = state.customRows?.find((r) => r.id === colKey);
              if (cRow && cRow.name !== '-') cName = cRow.name;
              else if (colKey.startsWith('prop_')) cName = colKey.substring(5);

              const aKey = Object.keys(row).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(cName),
              );
              if (aKey && row[aKey]) queryParts.push(String(row[aKey]).trim());
            }
          });

          if (queryParts.length === 0 && titleVal) {
            queryParts.push(titleVal);
          }
          const finalQueryParts = queryParts.filter(Boolean).join(', ');

          if (!finalQueryParts) {
            actions.setParsedData?.((prev) =>
              prev.map((r) =>
                r.id === row.id
                  ? { ...r, photoMatch: 'NO_MATCH', [photoColName]: '', matchedFiles: [] }
                  : r,
              ),
            );
            continue;
          }

          let baseName = titleVal
            ? String(titleVal)
                .replace(/[^a-z0-9]/gi, '_')
                .toLowerCase()
            : 'web_photo';

          actions.setManualMatchRowId?.(row.id);
          setWebSearchQuery(finalQueryParts);

          await new Promise((res) => setTimeout(res, 50));

          try {
            const { places } = await placesLib.Place.searchByText({
              textQuery: finalQueryParts,
              fields: ['id', 'displayName', 'photos'],
            });

            if (
              places &&
              places.length > 0 &&
              places[0].photos &&
              places[0].photos.length > 0
            ) {
              const highResUrl = places[0].photos[0].getURI({ maxWidth: 800 });
              const thumbUrl = places[0].photos[0].getURI({ maxWidth: 200 });
              const cleanFileName = `${baseName}_0.jpg`;

              window.dispatchEvent(
                new CustomEvent('loktr-bulk-scrape-add', {
                  detail: { title: titleVal || finalQueryParts, url: thumbUrl },
                }),
              );

              const result = await scrapeFunction({
                sourceUrl: highResUrl,
                fileName: cleanFileName,
                clientUrl: window.location.href,
              });

              const newFile = {
                name: cleanFileName,
                isGooglePhoto: false,
                isScrapedWebPhoto: true,
                scrapedUrl: result.data.url,
                preview: thumbUrl,
              };

              actions.setParsedData?.((prev) =>
                prev.map((r) =>
                  r.id === row.id
                    ? {
                        ...r,
                        photoMatch: 'MATCH',
                        [photoColName]: cleanFileName,
                        matchedFiles: [newFile],
                      }
                    : r,
                ),
              );
            } else {
              actions.setParsedData?.((prev) =>
                prev.map((r) =>
                  r.id === row.id
                    ? {
                        ...r,
                        photoMatch: 'NO_MATCH',
                        [photoColName]: '',
                        matchedFiles: [],
                      }
                    : r,
                ),
              );
            }

            await new Promise((res) => setTimeout(res, 200));
          } catch (e) {
            console.error('Auto scrape failed for', finalQueryParts, e);
            actions.setParsedData?.((prev) =>
              prev.map((r) =>
                r.id === row.id
                  ? {
                      ...r,
                      photoMatch: 'NO_MATCH',
                      [photoColName]: '',
                      matchedFiles: [],
                    }
                  : r,
              ),
            );
            await new Promise((res) => setTimeout(res, 200));
          }
        }
      } catch (error) {
        console.error('Error during bulk photo assignment:', error);
      } finally {
        await new Promise((res) => setTimeout(res, 1000));

        isBulkProcessingRef.current = false;
        setIsScrapingPhotos(false);
        window.dispatchEvent(new CustomEvent('loktr-bulk-scrape-end'));

        let nextRowIdToTrigger = null;

        const allRows = state.parsedData || [];
        if (allRows.length > 0) {
          const firstRow = allRows[0];
          nextRowIdToTrigger = firstRow.id;

          let titleVal = '';
          const titleColKey = Object.keys(state.rowMappings || {}).find(
            (k) =>
              state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
          );
          if (titleColKey) {
            let colName = titleColKey;
            const cRow = state.customRows?.find((r) => r.id === titleColKey);
            if (cRow && cRow.name !== '-') colName = cRow.name;
            else if (titleColKey.startsWith('prop_')) colName = titleColKey.substring(5);
            const actualKey = Object.keys(firstRow).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            if (actualKey) titleVal = String(firstRow[actualKey]).trim();
          }
          if (!titleVal)
            titleVal = String(
              firstRow['Title'] || firstRow['title'] || firstRow['TITLE'] || '',
            ).trim();

          const queryParts = [];
          [
            'TITLE',
            'LOCATION ADDRESS',
            'CITY',
            'STATE',
            'POSTCODE/ZIP',
            'COUNTRY',
          ].forEach((field) => {
            const colKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === field,
            );
            if (colKey) {
              let cName = colKey;
              const cRow = state.customRows?.find((r) => r.id === colKey);
              if (cRow && cRow.name !== '-') cName = cRow.name;
              else if (colKey.startsWith('prop_')) cName = colKey.substring(5);

              const aKey = Object.keys(firstRow).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(cName),
              );
              if (aKey && firstRow[aKey]) queryParts.push(String(firstRow[aKey]).trim());
            }
          });

          if (queryParts.length === 0 && titleVal) {
            queryParts.push(titleVal);
          }

          setWebSearchQuery(queryParts.filter(Boolean).join(', '));
          actions.setManualMatchRowId?.(firstRow.id);
        } else {
          setWebSearchQuery('');
        }

        setTimeout(() => setApplyToAllPhotos(false), 200);
        restoreGridFocus();

        if (goToNext && actions.setPendingNextColumn && nextRowIdToTrigger) {
          actions.setPendingNextColumn({
            rowId: nextRowIdToTrigger,
            colName: getPhotoColNameForNext(),
          });
        }
      }
      return;
    }

    if (localSelectedPhotos.length > 0) {
      setIsScrapingPhotos(true);
      try {
        const functions = getFunctions(getApp(), 'australia-southeast1');
        const scrapeFunction = httpsCallable(functions, 'scrapeAndStoreWebPhoto');

        const row = state.parsedData?.find((r) => r.id === state.manualMatchRowId);
        const existing = row?.matchedFiles || [];

        let baseName = 'web_photo';
        if (row) {
          const titleColKey = Object.keys(state.rowMappings || {}).find(
            (k) =>
              state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
          );
          if (titleColKey) {
            let colName = titleColKey;
            const cRow = state.customRows?.find((r) => r.id === titleColKey);
            if (cRow && cRow.name !== '-') colName = cRow.name;
            else if (titleColKey.startsWith('prop_')) colName = titleColKey.substring(5);

            const actualKey = Object.keys(row).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            if (actualKey && row[actualKey]) baseName = String(row[actualKey]);
          } else if (row['Title']) {
            baseName = String(row['Title']);
          } else if (row['title']) {
            baseName = String(row['title']);
          } else if (row['TITLE']) {
            baseName = String(row['TITLE']);
          }
        }

        baseName = baseName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const existingWebPhotos = existing.filter(
          (f) => f.isScrapedWebPhoto || f.isGooglePhoto,
        );
        let webIdx = existingWebPhotos.length;

        const processedFiles = await Promise.all(
          localSelectedPhotos.map(async (f) => {
            if (f.isGooglePhoto) {
              try {
                const cleanFileName = `${baseName}_${webIdx}.jpg`;
                webIdx++;

                const result = await scrapeFunction({
                  sourceUrl: f.sourceUrl,
                  fileName: cleanFileName,
                  clientUrl: window.location.href,
                });
                return {
                  ...f,
                  name: cleanFileName,
                  isGooglePhoto: false,
                  isScrapedWebPhoto: true,
                  scrapedUrl: result.data.url,
                  preview: result.data.url,
                };
              } catch (err) {
                console.error('Failed to scrape photo to temporary folder:', err);
                return f;
              }
            }
            return f;
          }),
        );

        const newFiles = [...existing, ...processedFiles];
        const uniqueFiles = [];
        const seen = new Set();

        newFiles.forEach((f) => {
          const id = f.isScrapedWebPhoto
            ? f.scrapedUrl
            : f.isGooglePhoto
              ? f.sourceUrl
              : f.name;
          if (!seen.has(id)) {
            seen.add(id);
            uniqueFiles.push(f);
          }
        });

        actions.handleManualMatch?.(uniqueFiles);
      } catch (error) {
        console.error('Error during photo assignment processing:', error);
      } finally {
        setIsScrapingPhotos(false);
        setLocalSelectedPhotos([]);
        restoreGridFocus();

        if (goToNext && actions.setPendingNextColumn) {
          actions.setPendingNextColumn({
            rowId: state.manualMatchRowId,
            colName: getPhotoColNameForNext(),
          });
        }
      }
    } else {
      setLocalSelectedPhotos([]);
      restoreGridFocus();

      if (goToNext && actions.setPendingNextColumn) {
        actions.setPendingNextColumn({
          rowId: state.manualMatchRowId,
          colName: getPhotoColNameForNext(),
        });
      }
    }
  };

  const handleCancelClick = () => {
    if (isScrapingPhotos && applyToAllPhotos) {
      cancelLoopRef.current = true;
    } else if (
      webPhotos.length > 0 ||
      currentRightPanelLocalFiles.length > 0 ||
      webSearchError !== ''
    ) {
      setWebPhotos([]);
      setCurrentRightPanelLocalFiles([]);
      setLocalSelectedPhotos([]);
      setWebSearchError('');
      restoreGridFocus();
    } else {
      handleManualMatchCancel();
    }
  };

  const handleManualMatchCancel = () => {
    setLocalSelectedPhotos([]);
    actions.setManualMatchRowId?.(null);
    restoreGridFocus();
  };

  const handleNavPrev = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.manualMatchRowId,
        colName: getPhotoColNameForNext(),
        direction: 'prev',
      });
    }
  };

  const handleNavNext = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.manualMatchRowId,
        colName: getPhotoColNameForNext(),
        direction: 'next',
      });
    }
  };

  const handleNavUp = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.manualMatchRowId,
        colName: getPhotoColNameForNext(),
        rowDirection: 'up',
      });
    }
  };

  const handleNavDown = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.manualMatchRowId,
        colName: getPhotoColNameForNext(),
        rowDirection: 'down',
      });
    }
  };

  const isOkDisabled = isScrapingPhotos;

  useEffect(() => {
    submitRef.current = () => {
      if (!isOkDisabled) {
        handleManualMatchOk(false);
      }
    };
    cancelRef.current = handleCancelClick;
    navPrevRef.current = handleNavPrev;
    navNextRef.current = handleNavNext;
    navUpRef.current = handleNavUp;
    navDownRef.current = handleNavDown;
  });

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (!state.manualMatchRowId) return;

      const activeEl = document.activeElement;

      const isTextInputReal =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable);
      const isControl =
        activeEl &&
        (activeEl.tagName === 'BUTTON' ||
          activeEl.type === 'radio' ||
          activeEl.type === 'checkbox' ||
          activeEl.getAttribute('role') === 'combobox' ||
          activeEl.classList?.contains('MuiSelect-select') ||
          activeEl.getAttribute('role') === 'menuitem' ||
          activeEl.getAttribute('role') === 'option' ||
          activeEl.getAttribute('role') === 'switch');

      const isListBox = document.querySelector('[role="listbox"]');

      if (e.key === 'Escape') {
        if (isListBox) return;

        if (!isTextInputReal && !isControl) {
          e.preventDefault();
          e.stopPropagation();
          restoreGridFocus();
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        if (cancelRef.current) cancelRef.current();
        return;
      }

      if (isListBox) return;

      // --- UNIVERSAL JUMP TO FOCUS VIA SPACE ---
      if (!isTextInputReal && !isControl && (e.key === ' ' || e.code === 'Space')) {
        e.preventDefault();
        e.stopPropagation();
        spaceTriggeredRef.current = true;

        const btn = document.getElementById('photo-search-button');
        if (btn && !btn.disabled) {
          btn.click();
        } else {
          let attempts = 0;
          const focusInterval = setInterval(() => {
            const input = document.getElementById('photo-search-input');
            if (input && !input.disabled) {
              input.focus();
              try {
                const len = input.value.length;
                input.setSelectionRange(len, len);
              } catch (err) {}
              clearInterval(focusInterval);
            }
            attempts++;
            if (attempts > 15) clearInterval(focusInterval);
          }, 50);
        }
        return;
      }

      if (isTextInputReal) {
        if (e.key !== 'Enter') return;
      }

      if (e.key === 'Enter') {
        const isComboBox =
          activeEl &&
          (activeEl.getAttribute('role') === 'combobox' ||
            activeEl.classList?.contains('MuiSelect-select'));
        if (isComboBox) return;
        enterOriginRef.current = 'submit';
        e.preventDefault();
        e.stopPropagation();
        setIsOkPressed(true);
        return;
      }

      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        if (e.key === 'ArrowLeft' && navPrevRef.current) navPrevRef.current();
        if (e.key === 'ArrowRight' && navNextRef.current) navNextRef.current();
        if (e.key === 'ArrowUp' && navUpRef.current) navUpRef.current();
        if (e.key === 'ArrowDown' && navDownRef.current) navDownRef.current();
      }
    };

    const handleGlobalKeyUp = (e) => {
      if (!state.manualMatchRowId) return;

      if (e.key === ' ' || e.code === 'Space') {
        if (spaceTriggeredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          spaceTriggeredRef.current = false;
          return;
        }
      }

      if (e.key === 'Escape') {
        const activeEl = document.activeElement;
        const isTextInputReal =
          activeEl &&
          (activeEl.tagName === 'INPUT' ||
            activeEl.tagName === 'TEXTAREA' ||
            activeEl.isContentEditable);
        const isControl =
          activeEl &&
          (activeEl.tagName === 'BUTTON' ||
            activeEl.type === 'radio' ||
            activeEl.type === 'checkbox' ||
            activeEl.getAttribute('role') === 'combobox' ||
            activeEl.classList?.contains('MuiSelect-select') ||
            activeEl.getAttribute('role') === 'menuitem' ||
            activeEl.getAttribute('role') === 'option' ||
            activeEl.getAttribute('role') === 'switch');

        if (!isTextInputReal && !isControl) {
          restoreGridFocus();
        }
        return;
      }

      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable))
        return;
      if (activeEl && activeEl.tagName === 'INPUT' && e.key !== 'Enter') return;

      if (e.key === 'Enter') {
        const origin = enterOriginRef.current;
        enterOriginRef.current = null;

        if (origin === 'listbox') {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            if (submitRef.current) submitRef.current();
          }, 150);
          return;
        }

        if (origin !== 'submit') return;

        e.preventDefault();
        e.stopPropagation();
        setIsOkPressed(false);
        if (submitRef.current) submitRef.current();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    window.addEventListener('keyup', handleGlobalKeyUp, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
      window.removeEventListener('keyup', handleGlobalKeyUp, { capture: true });
    };
  }, [state.manualMatchRowId, restoreGridFocus]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        py: 1,
        pl: 3,
        pr: '52px',
      }}>
      <Box sx={{ width: '100%', pb: 0, pt: 0 }}>
        <FormControl
          variant='outlined'
          fullWidth
          sx={{ height: '36px', justifyContent: 'center', mb: 1 }}>
          <OutlinedInput
            id='photo-search-input'
            placeholder='Search Google Places for Photos...'
            value={webSearchQuery}
            onChange={(e) => setWebSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearchWebPhotos();
            }}
            disabled={isSearchingWeb || isScrapingPhotos}
            sx={{
              borderRadius: '50px',
              height: '36px',
              backgroundColor:
                isSearchingWeb || isScrapingPhotos ? 'rgba(0,0,0,0.02)' : 'white',
              fontSize: '0.875rem',
              transition: 'all 0.3s ease',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.2)',
                borderWidth: '1px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  isSearchingWeb || isScrapingPhotos
                    ? 'rgba(0,0,0,0.2)'
                    : 'rgba(0,0,0,0.4)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0055ff',
                borderWidth: '2px',
              },
              '& .MuiInputBase-input': {
                textTransform: makeUppercase ? 'uppercase' : 'none',
              },
            }}
            startAdornment={
              <InputAdornment position='start' sx={{ pl: 0.5 }}>
                {isSearchingWeb ? (
                  <CircularProgress size={18} thickness={5} />
                ) : (
                  <IconButton
                    id='photo-search-button'
                    size='small'
                    onClick={handleSearchWebPhotos}
                    disabled={
                      isSearchingWeb || isScrapingPhotos || !webSearchQuery.trim()
                    }
                    sx={{
                      padding: '2px',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform:
                          isSearchingWeb || isScrapingPhotos ? 'none' : 'scale(1.15)',
                        backgroundColor: 'transparent',
                        color: isSearchingWeb || isScrapingPhotos ? 'inherit' : '#0055ff',
                      },
                    }}>
                    <SearchIcon fontSize='small' />
                  </IconButton>
                )}
              </InputAdornment>
            }
            endAdornment={
              webSearchQuery && (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={() => setWebSearchQuery('')}
                    size='small'
                    disabled={isSearchingWeb || isScrapingPhotos}
                    sx={{
                      p: '2px',
                      '&:hover': {
                        color: isSearchingWeb || isScrapingPhotos ? 'inherit' : '#ff1744',
                      },
                    }}>
                    <CancelIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        </FormControl>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
          {webSearchError && (
            <Typography color='error' variant='body2'>
              {webSearchError}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {currentRightPanelLocalFiles.map((file, idx) => {
                const isSelected = localSelectedPhotos.some((p) => p.name === file.name);
                const previewUrl = getFilePreview(file);
                const isVideo =
                  file.type?.startsWith('video/') ||
                  (file.name && file.name.match(/\.(mp4|mov|avi|wmv|webm)$/i)) ||
                  file.type === 'video';
                const isAudio =
                  file.type?.startsWith('audio/') ||
                  (file.name && file.name.match(/\.(mp3|wav|ogg|m4a)$/i)) ||
                  file.type === 'audio';

                return (
                  <Box
                    key={`local_${idx}`}
                    onClick={() => toggleLocalPhotoSelection(file)}
                    sx={{
                      width: 'calc(50% - 4px)',
                      aspectRatio: '1',
                      cursor: 'pointer',
                      border: isSelected ? '3px solid #1976d2' : '1px solid transparent',
                      padding: isSelected ? '2px' : '4px',
                      borderRadius: '6px',
                      backgroundColor: '#fff',
                      position: 'relative',
                      boxSizing: 'border-box',
                      display: 'flex',
                    }}>
                    {isVideo ? (
                      <video
                        src={`${previewUrl}#t=0.1`}
                        preload='metadata'
                        muted
                        playsInline
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '2px',
                        }}
                      />
                    ) : isAudio ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#f0f0f0',
                          borderRadius: '2px',
                          p: 1,
                          boxSizing: 'border-box',
                        }}>
                        <SoundWaveIcon sx={{ fontSize: 40, mb: 1 }} />
                        <audio
                          src={previewUrl}
                          controls
                          style={{ width: '100%', height: '30px' }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Box>
                    ) : (
                      <img
                        src={previewUrl}
                        alt={file.name}
                        loading='lazy'
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '2px',
                        }}
                      />
                    )}
                  </Box>
                );
              })}
              {webPhotos.map((url, idx) => {
                const isSelected = localSelectedPhotos.some((p) => p.sourceUrl === url);
                return (
                  <Box
                    key={`web_${idx}`}
                    onClick={() => toggleWebPhotoSelection(url, idx)}
                    sx={{
                      width: 'calc(50% - 4px)',
                      aspectRatio: '1',
                      cursor: 'pointer',
                      border: isSelected ? '3px solid #1976d2' : '1px solid transparent',
                      padding: isSelected ? '2px' : '4px',
                      borderRadius: '6px',
                      backgroundColor: '#fff',
                      position: 'relative',
                      boxSizing: 'border-box',
                      display: 'flex',
                    }}>
                    <img
                      src={url}
                      alt={`Web result ${idx}`}
                      loading='lazy'
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '2px',
                      }}
                    />
                  </Box>
                );
              })}
              {currentRightPanelLocalFiles.length === 0 &&
                webPhotos.length === 0 &&
                !isSearchingWeb && (
                  <Typography
                    color='text.secondary'
                    variant='body2'
                    sx={{
                      fontStyle: 'italic',
                      width: '100%',
                      textAlign: 'center',
                      mt: 2,
                    }}>
                    Upload local photos or hit enter above to fetch photos from Google
                    Maps.
                    <br />
                    Photos will appear in the left carousel.
                  </Typography>
                )}
            </Box>
          </Box>
        </Box>
      </Box>

      <DialogActionBar
        leftContent={
          <>
            <input
              type='file'
              multiple
              accept='image/*, video/*, audio/*'
              ref={localFileInputRef}
              style={{ display: 'none' }}
              onChange={handleLocalFileChange}
            />
            <IconButton
              onClick={() => localFileInputRef.current?.click()}
              disabled={isScrapingPhotos}
              size='small'
              title='Upload Local'
              sx={{ p: 0 }}>
              {isScrapingPhotos ? (
                <MenuUploadGrey
                  sx={{ fontSize: 48 }}
                  iconScale={8}
                  iconX={12}
                  iconY={5}
                  textSize='22px'
                  textY={108}
                  label='Upload'
                />
              ) : (
                <MenuUpload
                  sx={{ fontSize: 48 }}
                  iconScale={8}
                  iconX={12}
                  iconY={5}
                  textSize='22px'
                  textY={108}
                  label='Upload'
                />
              )}
            </IconButton>
          </>
        }
        applyToAllChecked={false}
        onApplyToAllChange={() => {}}
        disableApplyToAll={true}
        onCancel={handleCancelClick}
        disableCancel={isScrapingPhotos && !applyToAllPhotos}
        onUp={handleNavUp}
        onDown={handleNavDown}
        onPrev={handleNavPrev}
        onNext={handleNavNext}
        disableNav={isOkDisabled}
        onOk={() => handleManualMatchOk(false)}
        disableOk={isOkDisabled}
        isOkPressed={isOkPressed}
        isLoadingOk={isScrapingPhotos && applyToAllPhotos}
      />
    </Box>
  );
}
